import { Box, Text } from '@nimbus-ds/components';
import { useLayoutEffect, useRef, useState } from 'react';
import {
  areaPath,
  AXIS_CHAR_W,
  axisWidth,
  clampTooltipLeft,
  formatTickValue,
  linePath,
  markerRadius,
  maxXLabels,
  nearestIndex,
  niceTicks,
  shortDayLabel,
  thinIndices,
  tooltipWidth,
  type Point,
} from './trendChart.model.js';

/**
 * A dependency-free line/area chart with real axes for the analytics trends. Hand-rolled on
 * purpose — the admin ships no charting library and must not gain one.
 *
 * It used to be a bare sparkline: a filled area, a marker on the last point, and two captions
 * underneath (the first and last date). QA's report — "when you select last seven days there are
 * no dates in between" — was really two gaps: no intermediate x labels, and no y axis at all, so a
 * point's value was unreadable. Now there is a y axis with gridlines, an x label on every day that
 * fits (thinned as the range grows), and a marker on every point.
 *
 * 🚨 **It measures its own width instead of stretching a fixed `viewBox`.** The old chart drew into
 * a 300×72 box with `preserveAspectRatio="none"`, which is fine for a naked polyline (hence the
 * `vectorEffect="non-scaling-stroke"` that compensated for it) and fatal once there is text: the
 * glyphs would be stretched by whatever ratio the card happened to be. Rendering 1:1 into the
 * measured pixel width also lets the label thinning depend on the real width, which is the whole
 * point — these cards are ~240px wide three-up on a laptop and full width on a phone.
 */
export interface TrendChartProps {
  /** Series values, one per day, oldest → newest. */
  data: number[];
  /** UTC `YYYY-MM-DD` labels, parallel to `data`; drawn on the x axis, thinned to fit. */
  labels?: string[];
  /** Stroke colour (hex/CSS). The area and the markers are the same colour. */
  color?: string;
  /** Empty-state caption when there is no data. */
  emptyLabel?: string;
  /** Accessible name for the plot — the card's own title reads best here. */
  title?: string;
  /**
   * A day's value as the hover tooltip should say it — "5 orders", "1 form open", "€642.00".
   *
   * The chart deliberately does not know what it is plotting: the y axis abbreviates numbers and
   * the card heading formats the total, but neither can name the unit, and "5" on its own under a
   * date is not what QA asked for. Defaults to the plain number.
   */
  formatValue?: (value: number) => string;
}

/** Nimbus tokens, with the light-theme hex as the fallback so the SVG still paints in isolation. */
const INK = 'var(--nimbus-colors-neutral-textLow, #5d5d5d)';
const GRID = 'var(--nimbus-colors-neutral-surfaceDisabled, #e7e7e7)';
const AXIS = 'var(--nimbus-colors-neutral-surfaceHighlight, #d1d1d1)';

const PLOT_H = 104; // the drawing area, excluding axes
const PAD_TOP = 10; // keeps the top marker and gridline off the edge
const PAD_RIGHT = 8; // keeps the last marker inside the box
const TICK_GAP = 6; // baseline → first line of x-label text
const FONT = 10;
/** Width the layout assumes before the first measurement lands. */
const FALLBACK_W = 280;

/**
 * The empty-state gate. Kept hook-free and split from `TrendPlot` on purpose: a series of all
 * zeroes has no axis worth drawing, and this way the branch is assertable in a suite with no DOM
 * (the plot below cannot be — it measures itself).
 */
export function TrendChart({ data, emptyLabel, ...rest }: TrendChartProps): JSX.Element {
  const hasData = data.length > 0 && data.some((v) => v > 0);

  if (!hasData) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="72px"
        backgroundColor="neutral-surface"
        borderRadius="2"
      >
        <Text fontSize="caption" color="neutral-textLow">
          {emptyLabel ?? '—'}
        </Text>
      </Box>
    );
  }

  return <TrendPlot data={data} {...rest} />;
}

function TrendPlot({
  data,
  labels,
  color = '#2563EB',
  title,
  formatValue,
}: Omit<TrendChartProps, 'emptyLabel'>): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(FALLBACK_W);
  const [hovered, setHovered] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const measure = (): void => {
      const w = el.clientWidth;
      if (w > 0) setWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = data.length;
  // Counts (opens, orders) must not get fractional ticks; revenue may.
  const integerSeries = data.every((v) => Number.isInteger(v));
  const ticks = niceTicks(Math.max(...data), { integer: integerSeries });
  const tickText = ticks.map(formatTickValue);
  const top = ticks[ticks.length - 1]!;

  const left = axisWidth(tickText);
  const plotW = Math.max(40, width - left - PAD_RIGHT);
  const plotBottom = PAD_TOP + PLOT_H;

  const dayLabels = (labels ?? []).map(shortDayLabel);
  const shown = labels?.length ? thinIndices(n, maxXLabels(plotW)) : [];
  const widestLabel = shown.reduce((w, i) => Math.max(w, (dayLabels[i] ?? '').length), 0);
  // Rotated 45°, a label's vertical footprint is its width × sin45 (≈0.71), plus one line of text.
  const xLabelH = shown.length > 0 ? Math.ceil(widestLabel * AXIS_CHAR_W * 0.71) + FONT : 0;
  const height = plotBottom + TICK_GAP + xLabelH + 2;

  // With a single point, put it in the middle so the card still reads as a plot.
  const x = (i: number): number => left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number): number => plotBottom - (v / top) * PLOT_H;
  const points: Point[] = data.map((v, i) => ({ x: x(i), y: y(v) }));
  const r = markerRadius(n, plotW);

  const range = labels?.length ? ` ${labels[0]} – ${labels[labels.length - 1]}` : '';

  // The pointer's x arrives in CSS pixels but the plot is laid out in the width we last measured,
  // and the two drift apart for one frame after a resize (the SVG scales to `100%`, the state has
  // not caught up). Normalising through the live bounding box keeps the highlight under the cursor
  // rather than a few points off.
  const track = (clientX: number): void => {
    const rect = hostRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const px = (clientX - rect.left) * (width / rect.width);
    setHovered(nearestIndex(px, left, plotW, n));
  };

  // Re-checked against the CURRENT series, not just against null: switching the range from 90 days
  // to 7 while the pointer is over the plot leaves `hovered` pointing past the end of the new data.
  const hi = hovered !== null && hovered < n ? hovered : null;
  const hoverPoint = hi === null ? null : points[hi]!;
  const hoverLines =
    hi === null ? [] : [labels?.[hi] ?? dayLabels[hi] ?? '', (formatValue ?? String)(data[hi]!)];
  const tipW = tooltipWidth(hoverLines);

  return (
    <div ref={hostRef} style={{ width: '100%', position: 'relative' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={`${title ?? 'Trend'}${range}`}
        style={{ display: 'block', overflow: 'visible', touchAction: 'pan-y' }}
        onPointerMove={(e) => track(e.clientX)}
        onPointerDown={(e) => track(e.clientX)}
        onPointerLeave={() => setHovered(null)}
      >
        <title>{`${title ?? 'Trend'}${range}`}</title>

        {/* Y axis: a gridline and a value per tick. */}
        {ticks.map((t, i) => {
          const ty = y(t);
          return (
            <g key={`y-${t}`}>
              <line
                x1={left}
                x2={left + plotW}
                y1={ty}
                y2={ty}
                stroke={i === 0 ? AXIS : GRID}
                strokeWidth={1}
              />
              <text
                x={left - 6}
                y={ty}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={FONT}
                fill={INK}
              >
                {tickText[i]}
              </text>
            </g>
          );
        })}

        {n > 1 && (
          <path d={areaPath(points, plotBottom)} fill={color} fillOpacity={0.12} stroke="none" />
        )}
        {n > 1 && (
          <path
            d={linePath(points)}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {r > 0 &&
          points.map((p, i) => (
            <circle
              key={`p-${i}`}
              cx={p.x}
              cy={p.y}
              r={r}
              fill={color}
              stroke="var(--nimbus-colors-neutral-background, #fff)"
              strokeWidth={1}
            />
          ))}

        {/* The hovered day: a guide down the plot and a marker big enough to read as picked. It is
            drawn even where `markerRadius` suppressed the per-point dots — at 90 days the dots are
            a bar of ink, but the ONE the merchant is pointing at is exactly what they want to see. */}
        {hoverPoint && (
          <g pointerEvents="none">
            <line
              x1={hoverPoint.x}
              x2={hoverPoint.x}
              y1={PAD_TOP}
              y2={plotBottom}
              stroke={AXIS}
              strokeWidth={1}
            />
            <circle
              cx={hoverPoint.x}
              cy={hoverPoint.y}
              r={4}
              fill={color}
              stroke="var(--nimbus-colors-neutral-background, #fff)"
              strokeWidth={2}
            />
          </g>
        )}

        {/* X axis: every date that fits, rotated so short cards keep them legible. */}
        {shown.map((i) => {
          const px = x(i);
          return (
            <g key={`x-${i}`}>
              <line
                x1={px}
                x2={px}
                y1={plotBottom}
                y2={plotBottom + 3}
                stroke={AXIS}
                strokeWidth={1}
              />
              <text
                x={px}
                y={plotBottom + TICK_GAP + FONT - 2}
                textAnchor="end"
                fontSize={FONT}
                fill={INK}
                transform={`rotate(-45 ${px} ${plotBottom + TICK_GAP + FONT - 2})`}
              >
                {dayLabels[i]}
              </text>
            </g>
          );
        })}
      </svg>

      {/* HTML, not SVG text: it sizes itself to whatever the formatter returned, wraps a long money
          string, and takes the Nimbus surface tokens as-is. `pointerEvents: none` so it can never
          sit between the cursor and the plot and strobe the highlight on and off. */}
      {hoverPoint && (
        <div
          style={{
            position: 'absolute',
            left: `${clampTooltipLeft(hoverPoint.x, tipW, width)}px`,
            top: `${Math.max(0, hoverPoint.y - 46)}px`,
            width: `${tipW}px`,
            pointerEvents: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            background: 'var(--nimbus-colors-neutral-background, #fff)',
            border: '1px solid var(--nimbus-colors-neutral-surfaceHighlight, #d1d1d1)',
            boxShadow: '0 2px 8px rgba(0,0,0,.12)',
            lineHeight: 1.3,
          }}
        >
          <Text fontSize="caption" color="neutral-textLow">
            {hoverLines[0]}
          </Text>
          <Text fontSize="caption" fontWeight="bold">
            {hoverLines[1]}
          </Text>
        </div>
      )}
    </div>
  );
}
